import type { WeldSample, WeldTorch } from './weldTxtParser'
import { resolvePassMapping } from './weldUpload/passMappingKeys'

/**
 * Logical parameter keys the aggregator understands. The caller chooses which
 * subset to compute per call. Every key has a single source-column mapping on
 * the post-parser `WeldSample` shape (single-torch), so there is no Lead/Trail
 * branching in the aggregator anymore — the parser already picked the active
 * torch for each row.
 */
export type ParameterKey =
  | 'preheat'
  | 'volts'
  | 'amps'
  | 'wireFeed'
  | 'travelSpeed'
  | 'heatInput'
  | 'tilt'
  | 'distance'
  | 'oscWidth'
  | 'target'
  | 'horizontalBias'
  | 'frequency'
  | 'totalWireConsumed'
  | 'trueEnergy'

/** Canonical display order. Preheat is first because it's the only manual-only parameter. */
export const ALL_PARAMETERS: ParameterKey[] = [
  'preheat',
  'volts',
  'amps',
  'wireFeed',
  'travelSpeed',
  'heatInput',
  'tilt',
  'distance',
  'oscWidth',
  'target',
  'horizontalBias',
  'frequency',
  'totalWireConsumed',
  'trueEnergy'
]

export type Torch = WeldTorch

export interface AggregateMinAvgMax {
  min: number | null
  avg: number | null
  max: number | null
}

export interface AggregateRow {
  pass: string
  /** Mapped WPS pass label when ALX pass name differs (e.g. CW/CCW rows). */
  wpsPass?: string
  /** Sorted set of torches that contributed samples to this pass. */
  torches: WeldTorch[]
  sampleCount: number
  values: Record<ParameterKey, AggregateMinAvgMax>
}

export interface AggregateResult {
  rows: AggregateRow[]
}

const NULL_TRIPLE: AggregateMinAvgMax = { min: null, avg: null, max: null }

/**
 * Source-column extractor per parameter (single torch — the parser already
 * picked active-torch fields). Preheat has no TXT column; the user enters it
 * manually on the WeldParameters page.
 */
const SOURCE_EXTRACTORS: Record<ParameterKey, (s: WeldSample) => number | null> = {
  preheat: () => null,
  volts: (s) => s.volts,
  amps: (s) => s.amps,
  wireFeed: (s) => s.wireIpm,
  travelSpeed: (s) => s.travelSpeed,
  heatInput: (s) => s.calcHeat,
  tilt: (s) => s.tilt,
  distance: (s) => s.distance,
  oscWidth: (s) => s.widthMil,
  target: (s) => s.targetVlt,
  horizontalBias: (s) => s.csBias,
  frequency: (s) => s.bpm,
  totalWireConsumed: (s) => s.totWireIn,
  trueEnergy: (s) => s.trueEnergy
}

export function aggregateWeldParameters(
  samples: WeldSample[],
  passMappings: Record<string, string>,
  parameters: ParameterKey[]
): AggregateResult {
  const groups = new Map<string, { pass: string; samples: WeldSample[] }>()

  for (const sample of samples) {
    const mapped = resolvePassMapping(sample.passRaw, sample.torch, passMappings)
    if (!mapped) continue
    let group = groups.get(mapped)
    if (!group) {
      group = { pass: mapped, samples: [] }
      groups.set(mapped, group)
    }
    group.samples.push(sample)
  }

  const rows: AggregateRow[] = []
  // Array.from to keep ES5 tsconfig target happy (no downlevelIteration).
  for (const group of Array.from(groups.values())) {
    const values = {} as Record<ParameterKey, AggregateMinAvgMax>
    for (const param of parameters) {
      values[param] = computeStats(group.samples, param)
    }
    const torchSet = new Set<WeldTorch>()
    for (const s of group.samples) torchSet.add(s.torch)
    const torches = Array.from(torchSet).sort() as WeldTorch[]
    rows.push({
      pass: group.pass,
      torches,
      sampleCount: group.samples.length,
      values
    })
  }

  return { rows }
}

function computeStats(samples: WeldSample[], param: ParameterKey): AggregateMinAvgMax {
  const extract = SOURCE_EXTRACTORS[param]
  const values: number[] = []
  for (const sample of samples) {
    const raw = extract(sample)
    if (raw !== null && Number.isFinite(raw) && raw !== 0) {
      values.push(raw)
    }
  }
  if (values.length === 0) return { ...NULL_TRIPLE }

  const trimmed = trimOutliersIqr(values)
  const min = Math.min(...trimmed)
  const max = Math.max(...trimmed)
  const sum = trimmed.reduce((acc, v) => acc + v, 0)
  const avg = sum / trimmed.length

  return { min, avg, max }
}

/** Drop values outside Tukey fences (Q1 − 1.5·IQR … Q3 + 1.5·IQR). */
export function trimOutliersIqr(values: number[]): number[] {
  if (values.length < 4) return values
  const sorted = [...values].sort((a, b) => a - b)
  const q1 = quantileSorted(sorted, 0.25)
  const q3 = quantileSorted(sorted, 0.75)
  const iqr = q3 - q1
  if (iqr === 0) return values
  const lo = q1 - 1.5 * iqr
  const hi = q3 + 1.5 * iqr
  const kept = values.filter((v) => v >= lo && v <= hi)
  return kept.length > 0 ? kept : values
}

function quantileSorted(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  const next = sorted[base + 1]
  if (next === undefined) return sorted[base]
  return sorted[base] + rest * (next - sorted[base])
}

/**
 * Server aggregate parameter keys (matching `STATS_COLUMN_KEYS` plus `tilt`
 * and `distance`) map to the canonical client `ParameterKey` set used by
 * `WeldParametersUploadedView`. Returns `null` for unknown server keys.
 */
const SERVER_TO_CLIENT_PARAM: Record<string, ParameterKey | undefined> = {
  travelSpeed: 'travelSpeed',
  volts: 'volts',
  amps: 'amps',
  wireIpm: 'wireFeed',
  widthMil: 'oscWidth',
  targetVlt: 'target',
  csBias: 'horizontalBias',
  bpm: 'frequency',
  totWireIn: 'totalWireConsumed',
  trueEnergy: 'trueEnergy',
  heat: 'heatInput',
  tilt: 'tilt',
  distance: 'distance'
}

interface ServerAggregateRow {
  pass: string
  sampleCount: number
  torches: WeldTorch[]
  values: Record<string, AggregateMinAvgMax>
  overrides: Record<string, AggregateMinAvgMax & { sampleCount: number }>
}

interface ServerAggregateResponseShape {
  rows: ServerAggregateRow[]
}

/**
 * Translate a `GET /telemetry/aggregate` response into the same
 * `AggregateResult` shape that `aggregateWeldParameters` produces locally, so
 * the WeldParametersUploadedView can consume either source uniformly.
 *
 * The server uses `STATS_COLUMN_KEYS`-style keys; the uploaded view uses
 * `ParameterKey` keys. Overrides win over base values per parameter.
 */
export function aggregateFromServerResponse(
  response: ServerAggregateResponseShape
): AggregateResult {
  const rows: AggregateRow[] = response.rows.map((row) => {
    const values = {} as Record<ParameterKey, AggregateMinAvgMax>
    for (const param of ALL_PARAMETERS) {
      values[param] = { ...NULL_TRIPLE }
    }
    for (const [serverKey, stats] of Object.entries(row.values || {})) {
      const clientKey = SERVER_TO_CLIENT_PARAM[serverKey]
      if (!clientKey) continue
      values[clientKey] = {
        min: stats.min,
        avg: stats.avg,
        max: stats.max
      }
    }
    for (const [serverKey, override] of Object.entries(row.overrides || {})) {
      const clientKey = SERVER_TO_CLIENT_PARAM[serverKey]
      if (!clientKey) continue
      values[clientKey] = {
        min: override.min,
        avg: override.avg,
        max: override.max
      }
    }
    return {
      pass: row.pass,
      torches: row.torches,
      sampleCount: row.sampleCount,
      values
    }
  })
  return { rows }
}
